/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  Address,
  BuildingOwner,
  CONTACT_TABLE_NAME,
  Contact,
  ContactProfileType,
  ContactStatus,
  ContactType,
  CreateBuildingOwnerInput,
  CreateBuildingOwnerMutationVariables,
  CreateContactMutationVariables as CreateMutationVariables,
  CreateUnitOwnerInput,
  CreateUnitOwnerMutationVariables,
  CustomSubscription,
  CustomSubscriptionType,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  DeepDeletableEntityType,
  DeleteEntityByIdMutationVariables,
  DeleteContactMutationVariables as DeleteMutationVariables,
  JointOwner,
  Model,
  MutationStatus,
  OnCreateBuildingOwnerSubscription,
  OnCreateContactSubscription,
  OnCreateCustomSubscriptionSubscription,
  OnCreateUnitOwnerSubscription,
  OnDeleteBuildingOwnerSubscription,
  OnDeleteContactSubscription,
  OnDeleteCustomSubscriptionSubscription,
  OnDeleteUnitOwnerSubscription,
  OnUpdateBuildingOwnerSubscription,
  OnUpdateContactSubscription,
  OnUpdateCustomSubscriptionSubscription,
  OnUpdateUnitOwnerSubscription,
  UnitOwner,
  UserRightChangeEvent,
  cleanInputCreate,
  cleanInputUpdate,
  contactContainsType,
  createBuildingOwner as createBuildingOwnerMutation,
  createContact as createMutation,
  createUnitOwner as createUnitOwnerMutation,
  deleteBuildingOwner as deleteBuildingOwnerMutation,
  deleteEntityById as deleteEntityByIdMutation,
  deleteContact as deleteMutation,
  deleteUnitOwner as deleteUnitOwnerMutation,
  getContactNameFromObject,
  getContactStatus,
  getContact as getQuery,
  getReadId,
  getTableClientId,
  getTypedCustomSubscriptionValueForRefetch,
  handleUserRightsChange,
  isNilOrEmpty,
  isValidContact,
  onCreateBuildingOwner,
  onCreateContact,
  onCreateCustomSubscription,
  onCreateUnitOwner,
  onDeleteBuildingOwner,
  onDeleteContact,
  onDeleteCustomSubscription,
  onDeleteUnitOwner,
  onUpdateBuildingOwner,
  onUpdateContact,
  onUpdateCustomSubscription,
  onUpdateUnitOwner,
  removeNestedFieldsExcept,
  resolveOneToMany,
  resolveOneToOne,
  syncBuildingOwners,
  syncUnitOwners,
  syncUnitOwnersForFolders,
  updateBuildingOwner as updateBuildingOwnerMutation,
  updateContact as updateMutation,
  updateUnitOwner as updateUnitOwnerMutation,
  todayDateTime,
  verifyPeppolContact as verifyPeppolContactMutation,
  mergeFullyFetchedRecords,
} from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  deleteAndHideEntity,
  deleteEntityWithFetchBefore,
  get,
  getFilterFieldNameForIndex,
  list,
  mutation,
  mutationWithFetchBefore,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import { ReactNode, useContext, createContext, useEffect, useMemo } from 'react';

import { VerifyPeppolContactResponse } from 'src/API';
import { inviteMember } from 'src/auth/client';
import { pendingInvitationsQueryKey } from 'src/hooks/usePendingInvitations';
import { workspaceInvitationsQueryKey } from 'src/hooks/useWorkspaceInvitations';
import { getExistingJointOwnershipOfOwners } from 'src/utils/contactutils';
import { PeppolRegistrationCheck } from 'src/utils/peppolRegistrationUtils';

import { AddressContext, useAddresses } from './AddressContext';
import { useCommunicationSettingsProfiles } from './CommunicationSettingsProfilesContext';
import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { useEntityRelations } from './EntityRelationsContext';
import { useUser } from './UserContext';
import { syncContactsForFolders } from './utils/cleanedQueries';
import { CONTACT_QUERY, CONTACTS_QUERY } from './utils/contactQueryUtils';
import { usePermissions } from './utils/PermissionsContext';

const ENTITY_MODEL_NAME: Model = 'Contact';
const ENTITY_MODEL_NAME_BUILDING_OWNER: Model = 'BuildingOwner';
const ENTITY_MODEL_NAME_UNIT_OWNER: Model = 'UnitOwner';

const ALLOWED_TYPES_BEFORE_NEW_TYPE: Partial<Record<ContactType, ContactType[]>> = {
  [ContactType.OWNER]: [ContactType.MEMBER, ContactType.OTHER],
  [ContactType.TENANT]: [ContactType.OTHER],
  [ContactType.GUARANTOR]: [ContactType.OTHER],
  [ContactType.CONTRACTOR]: [ContactType.OTHER],
  [ContactType.HOA]: [
    ContactType.OWNER,
    ContactType.TENANT,
    ContactType.GUARANTOR,
    ContactType.CONTRACTOR,
    ContactType.OTHER,
  ],
};

const COMPANY_ONLY_NEW_CONTACT_TYPES: ContactType[] = [ContactType.HOA];

const BACKEND_ONLY_CONTACT_FIELDS: (keyof Contact)[] = [
  'defaultBankAccountId',
  'supplierPaymentMode',
  'supplierPaymentModeExceptions',
  'supplierPaymentModeHistory',
];

export type ActionContact =
  | {
      type: 'SHOULD_FETCH_CONTACT' | 'IS_FETCHING_CONTACT';
    }
  | {
      type: 'ADD_CONTACT';
      payload: { contact: Contact };
    }
  | {
      type: 'FETCHED_CONTACT';
      payload: { contacts: Contact[]; idsAtFetchStart?: Set<string> };
    }
  | {
      type: 'UPDATE_CONTACT';
      payload: { contact: Contact };
    }
  | {
      type: 'DELETE_CONTACT';
      payload: { id: string };
    };

export type ActionBuildingOwner =
  | {
      type: 'SHOULD_FETCH_BUILDING_OWNER' | 'IS_FETCHING_BUILDING_OWNER';
    }
  | {
      type: 'ADD_BUILDING_OWNER';
      payload: { buildingOwner: BuildingOwner };
    }
  | {
      type: 'FETCHED_BUILDING_OWNER';
      payload: { buildingOwners: BuildingOwner[] };
    }
  | {
      type: 'UPDATE_BUILDING_OWNER';
      payload: { buildingOwner: BuildingOwner };
    }
  | {
      type: 'DELETE_BUILDING_OWNER';
      payload: { id: string };
    };

export type ActionUnitOwner =
  | {
      type: 'SHOULD_FETCH_UNIT_OWNER' | 'IS_FETCHING_UNIT_OWNER';
    }
  | {
      type: 'ADD_UNIT_OWNER';
      payload: { unitOwner: UnitOwner };
    }
  | {
      type: 'FETCHED_UNIT_OWNER';
      payload: { unitOwners: UnitOwner[] };
    }
  | {
      type: 'UPDATE_UNIT_OWNER';
      payload: { unitOwner: UnitOwner };
    }
  | {
      type: 'DELETE_UNIT_OWNER';
      payload: { id: string };
    };

type ContactNoId = Omit<Contact, 'id' | 'clientId' | 'readId'>;

interface CreateContact extends Omit<ContactNoId, 'address'> {
  address?: Omit<Address, 'id' | 'clientId' | 'readId'> | null;
}

export interface ContactContext extends ContactClassification {
  getContact: (id: string) => Contact | undefined;
  createBuildingOwner: (
    input: Omit<BuildingOwner, 'id' | 'readId'> | Omit<BuildingOwner, 'id' | 'clientId' | 'readId'>
  ) => Promise<BuildingOwner>;
  updateBuildingOwner: (original: BuildingOwner, updates: Partial<BuildingOwner>) => Promise<void>;
  deleteBuildingOwner: (id: string) => Promise<void>;
  deleteUnitOwner: (id: string) => Promise<void>;
  updateUnitOwner: (original: UnitOwner, updates: Partial<UnitOwner>) => Promise<void>;
  createUnitOwner: (input: Omit<UnitOwner, 'id' | 'clientId' | 'readId'>) => Promise<UnitOwner>;
  createContact: (input: CreateContact) => Promise<Contact>;
  createContactWithoutAddress: (input: Omit<Contact, 'id' | 'clientId' | 'readId' | 'address'>) => Promise<Contact>;
  updateContact: (original: Contact, updates: Partial<Contact>) => Promise<Contact>;
  refreshContact: (id: string) => Promise<void>;
  setContact: (contact: Contact) => void;
  deleteContact: (id: string) => Promise<void>;
  deepDeleteContact: (contactId: string) => Promise<MutationStatus | undefined>;
  inviteContactToClientAccount: (contact: Contact) => Promise<MutationStatus>;
  addOwnerTypeIfAllowed: (contact: Contact) => Promise<Contact>;
  addTypeToContactIdIfAllowed: (contactId: string, newType: ContactType) => Promise<void>;
  addTypeIfAllowed: (contact: Contact, newType: ContactType) => Promise<Contact>;
  createAddress: AddressContext['createAddress'];
  updateAddress: AddressContext['updateAddress'];
  deleteAddress: AddressContext['deleteAddress'];
  getOrCreateJointOwnership: (existingJointOwnerships: Contact[], ownerIds: string[]) => Promise<Contact>;
  contactsLoading: boolean;
  contactsError: string | undefined;
  setFetchContacts: () => void;
  setFetchBuildingOwners: () => void;
  setFetchUnitOwners: () => void;
  checkCompanyPeppolStatus: (contactId: string) => Promise<PeppolRegistrationCheck>;
}

export const contactReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_CONTACT':
      if (state.Contact.loading || state.Contact.shouldFetch) {
        return state;
      }
      return {
        ...state,
        Contact: { ...state.Contact, shouldFetch: true },
      };
    case 'IS_FETCHING_CONTACT':
      if (state.Contact.loading) {
        return state;
      }
      return {
        ...state,
        Contact: { ...state.Contact, loading: isNil(state.Contact.lastFetch) },
      };
    case 'FETCHED_CONTACT':
      return {
        ...state,
        Contact: {
          ...state.Contact,
          data: mergeFullyFetchedRecords(state.Contact.data, action.payload.contacts, action.payload.idsAtFetchStart),
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_CONTACT':
      // Check if already present - If already added by this user or coming from another user
      if (state.Contact.data?.find((object) => object.id === action.payload.contact.id)) {
        return state;
      }

      return {
        ...state,
        Contact: {
          ...state.Contact,
          data: [...state.Contact.data, action.payload.contact],
        },
      };
    case 'UPDATE_CONTACT':
      // No data
      if (isNilOrEmpty(state.Contact.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.Contact.data?.find((object) => object.id === action.payload.contact.id);
      if (!currentObject) {
        return {
          ...state,
          Contact: {
            ...state.Contact,
            data: [...state.Contact.data, action.payload.contact],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.contact)) {
        return state;
      }
      // Update
      return {
        ...state,
        Contact: {
          ...state.Contact,
          data: state.Contact.data.map((object) => {
            if (object.id === action.payload.contact.id) {
              return action.payload.contact;
            }
            return object;
          }),
        },
      };
    case 'DELETE_CONTACT':
      if (isNilOrEmpty(state.Contact.data)) {
        return state;
      }
      return {
        ...state,
        Contact: {
          ...state.Contact,
          data: state.Contact.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

export const buildingOwnerReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_BUILDING_OWNER':
      if (state.BuildingOwner.loading || state.BuildingOwner.shouldFetch) {
        return state;
      }
      return {
        ...state,
        BuildingOwner: { ...state.BuildingOwner, shouldFetch: true },
      };
    case 'IS_FETCHING_BUILDING_OWNER':
      if (state.BuildingOwner.loading) {
        return state;
      }
      return {
        ...state,
        BuildingOwner: { ...state.BuildingOwner, loading: isNil(state.BuildingOwner.lastFetch) },
      };
    case 'FETCHED_BUILDING_OWNER':
      return {
        ...state,
        BuildingOwner: {
          ...state.BuildingOwner,
          data: action.payload.buildingOwners,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_BUILDING_OWNER':
      // Check if already present - If already added by this user or coming from another user
      if (state.BuildingOwner.data?.find((object) => object.id === action.payload.buildingOwner.id)) {
        return state;
      }

      return {
        ...state,
        BuildingOwner: {
          ...state.BuildingOwner,
          data: [...state.BuildingOwner.data, action.payload.buildingOwner],
        },
      };
    case 'UPDATE_BUILDING_OWNER':
      // No data
      if (isNilOrEmpty(state.BuildingOwner.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.BuildingOwner.data?.find((object) => object.id === action.payload.buildingOwner.id);
      if (!currentObject) {
        return {
          ...state,
          BuildingOwner: {
            ...state.BuildingOwner,
            data: [...state.BuildingOwner.data, action.payload.buildingOwner],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.buildingOwner)) {
        return state;
      }

      // Update
      return {
        ...state,
        BuildingOwner: {
          ...state.BuildingOwner,
          data: state.BuildingOwner.data.map((object) => {
            if (object.id === action.payload.buildingOwner.id) {
              return action.payload.buildingOwner;
            }
            return object;
          }),
        },
      };
    case 'DELETE_BUILDING_OWNER':
      if (isNilOrEmpty(state.BuildingOwner.data)) {
        return state;
      }
      return {
        ...state,
        BuildingOwner: {
          ...state.BuildingOwner,
          data: state.BuildingOwner.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

export const unitOwnerReducerDelegation = (
  state: ContextLoaderStore,
  action: ContextLoaderAction
): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_UNIT_OWNER':
      if (state.UnitOwner.loading || state.UnitOwner.shouldFetch) {
        return state;
      }
      return {
        ...state,
        UnitOwner: { ...state.UnitOwner, shouldFetch: true },
      };
    case 'IS_FETCHING_UNIT_OWNER':
      if (state.UnitOwner.loading) {
        return state;
      }
      return {
        ...state,
        UnitOwner: { ...state.UnitOwner, loading: isNil(state.UnitOwner.lastFetch) },
      };
    case 'FETCHED_UNIT_OWNER':
      return {
        ...state,
        UnitOwner: {
          ...state.UnitOwner,
          data: action.payload.unitOwners,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_UNIT_OWNER':
      // Check if already present - If already added by this user or coming from another user
      if (state.UnitOwner.data?.find((object) => object.id === action.payload.unitOwner.id)) {
        return state;
      }

      return {
        ...state,
        UnitOwner: {
          ...state.UnitOwner,
          data: [...state.UnitOwner.data, action.payload.unitOwner],
        },
      };
    case 'UPDATE_UNIT_OWNER':
      // No data
      if (isNilOrEmpty(state.UnitOwner.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.UnitOwner.data?.find((object) => object.id === action.payload.unitOwner.id);
      if (!currentObject) {
        return {
          ...state,
          UnitOwner: {
            ...state.UnitOwner,
            data: [...state.UnitOwner.data, action.payload.unitOwner],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.unitOwner)) {
        return state;
      }

      // Update
      return {
        ...state,
        UnitOwner: {
          ...state.UnitOwner,
          data: state.UnitOwner.data.map((object) => {
            if (object.id === action.payload.unitOwner.id) {
              return action.payload.unitOwner;
            }
            return object;
          }),
        },
      };
    case 'DELETE_UNIT_OWNER':
      if (isNilOrEmpty(state.UnitOwner.data)) {
        return state;
      }
      return {
        ...state,
        UnitOwner: {
          ...state.UnitOwner,
          data: state.UnitOwner.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

export const fetchBuildingOwners = async (
  by: 'byClientId' | 'byBuilding' | 'byContact',
  byValue: string,
  additionalFilter?: object
): Promise<BuildingOwner[]> => {
  return await list<BuildingOwner>(
    removeNestedFieldsExcept(syncBuildingOwners, DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY, ['building'], ['owner']),
    getFilterFieldNameForIndex(by),
    byValue,
    additionalFilter
  );
};

export const fetchUnitOwners = async (
  by: 'byClientId' | 'byUnit' | 'byOwner',
  byValue: string,
  additionalFilter?: object
): Promise<UnitOwner[]> => {
  return await list<UnitOwner>(syncUnitOwners, getFilterFieldNameForIndex(by), byValue, additionalFilter);
};

export const fetchUnitOwnersForFolders = async (clientId: string): Promise<UnitOwner[]> =>
  await list<UnitOwner>(syncUnitOwnersForFolders, getFilterFieldNameForIndex('byClientId'), clientId);

const fetchContacts = async (clientId: string, additionalFilter?: object): Promise<Contact[]> => {
  const contacts = await list<Contact>(
    CONTACTS_QUERY,
    getFilterFieldNameForIndex('byClientId'),
    clientId,
    additionalFilter
  );
  const cleanedContacts = contacts.map((contact) => cleanInputUpdate(contact, false, true)) as Contact[];
  return cleanedContacts;
};

export const fetchContactsForFolders = async (clientId: string): Promise<Contact[]> =>
  (
    await list<Contact>(
      syncContactsForFolders,
      getFilterFieldNameForIndex('byClientId'),
      getTableClientId(clientId, ENTITY_MODEL_NAME)
    )
  ).filter((contact) => contact.status !== ContactStatus.DRAFT_OCR);

interface ContactClassification {
  contacts: Contact[];
  validContacts: Contact[];
  companies: Contact[];
  individuals: Contact[];
  tenants: Contact[];
  guarantors: Contact[];
  owners: Contact[];
  others: Contact[];
  members: Contact[];
  contractors: Contact[];
  jointOwners: Contact[];
  ownersAndJointOwners: Contact[];
  displayContacts: Contact[];
  ocrDrafts: Contact[];
  client: Contact | null;
}

export const ContactContext = createContext<ContactContext | null>(null);

export const ContactContextProvider = ({ children }: { children?: ReactNode }) => {
  const {
    BuildingOwner: { data: buildingOwners, loading: buildingOwnersLoading, shouldFetch: shouldFetchBuildingOwner },
    Contact: { data: contactLoader, loading: contactsLoading, shouldFetch: shouldFetchContact },
    LeaseContact: { data: leaseContacts, loading: leaseContactsLoading },
    UnitOwner: { data: unitOwners, loading: unitOwnersLoading, shouldFetch: shouldFetchUnitOwner },
    User: { data: users },
    Address: { data: address, loading: addressesLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const { entityRelations, loading: entityRelationLoading } = useEntityRelations();
  const { buildingsUnitsDetailsDelete, contactsDetailsDelete } = usePermissions();
  const { clientId } = useUser();
  const { createAddress, updateAddress, deleteAddress } = useAddresses();
  const { getDefaultOwnerCommunicationSettingsProfiles, loading: communicationSettingsProfilesLoading } =
    useCommunicationSettingsProfiles();
  const queryClient = useQueryClient();

  const loading =
    buildingOwnersLoading ||
    contactsLoading ||
    entityRelationLoading ||
    leaseContactsLoading ||
    unitOwnersLoading ||
    addressesLoading ||
    communicationSettingsProfilesLoading;

  const contacts = useMemo<ContactClassification>(() => {
    const contactClassification: ContactClassification = {
      contacts: [],
      validContacts: [],
      companies: [],
      individuals: [],
      tenants: [],
      guarantors: [],
      owners: [],
      others: [],
      members: [],
      contractors: [],
      jointOwners: [],
      ownersAndJointOwners: [],
      displayContacts: [],
      ocrDrafts: [],
      client: null,
    };
    const typeMap: Record<ContactType, keyof ContactClassification> = {
      [ContactType.CLIENT]: 'client',
      [ContactType.MEMBER]: 'members',
      [ContactType.TENANT]: 'tenants',
      [ContactType.OWNER]: 'owners',
      [ContactType.CONTRACTOR]: 'contractors',
      [ContactType.JOINT_OWNERSHIP]: 'jointOwners',
      [ContactType.GUARANTOR]: 'guarantors',
      [ContactType.OTHER]: 'others',
      [ContactType.HOA]: 'others',
    };

    const classification = contactLoader.reduce((result: ContactClassification, contact: Contact) => {
      const contactObj = {
        ...contact,
        leases: resolveOneToMany(contact.id, leaseContacts, 'contactId'),
        units: resolveOneToMany(contact.id, unitOwners, 'ownerId'),
        buildings: resolveOneToMany(contact.id, buildingOwners, 'contactId'),
        ...(contact.addressId ? { address: resolveOneToOne(contact.addressId, address, 'id') } : {}),
        sourceEntityRelations: resolveOneToMany(contact.id, entityRelations, 'sourceId'),
        foreignEntityRelations: resolveOneToMany(contact.id, entityRelations, 'foreignId'),
      };
      if (contact.status === ContactStatus.DRAFT_OCR) {
        result.ocrDrafts.push(contactObj);
        return result;
      }
      if (isValidContact(contactObj)) {
        result.validContacts.push(contactObj);
      }
      if (contact.profileType === ContactProfileType.COMPANY) {
        result.companies.push(contactObj);
      } else {
        result.individuals.push(contactObj);
      }
      const types = contact.types;
      const effectiveTypes = !isEmpty(types) ? types : [ContactType.OTHER];
      if (isValidContact(contactObj)) {
        for (const contactType of effectiveTypes) {
          const type = typeMap[contactType];
          if (type === 'client') {
            result.client = contactObj;
          } else if (isArray(result[type])) {
            (result[type] as Contact[]).push(contactObj);
          }
        }
      }
      // ALL-tab list: include unless the contact is purely MEMBER or CLIENT
      const isPureMemberOrClient =
        effectiveTypes.length === 1 &&
        (effectiveTypes[0] === ContactType.CLIENT || effectiveTypes[0] === ContactType.MEMBER);
      if (!isPureMemberOrClient) {
        result.displayContacts.push(contactObj);
      }
      result.contacts.push(contactObj);
      return result;
    }, contactClassification);

    classification.ownersAndJointOwners = [...classification.owners, ...classification.jointOwners];
    return classification;
  }, [contactLoader, unitOwners, buildingOwners, leaseContacts, address, entityRelations]);

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_BUILDING_OWNER' });
      try {
        const result = await fetchBuildingOwners(
          'byClientId',
          getTableClientId(clientId!, ENTITY_MODEL_NAME_BUILDING_OWNER)
        );
        contextDispatch({ type: 'FETCHED_BUILDING_OWNER', payload: { buildingOwners: result } });
      } catch (error) {
        console.error('[contacts] fetching building owners failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'BuildingOwner' } });
      }
    };
    if (shouldFetchBuildingOwner) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchBuildingOwner]);

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_UNIT_OWNER' });
      try {
        const result = await fetchUnitOwners('byClientId', getTableClientId(clientId!, ENTITY_MODEL_NAME_UNIT_OWNER));
        contextDispatch({ type: 'FETCHED_UNIT_OWNER', payload: { unitOwners: result } });
      } catch (error) {
        console.error('[contacts] fetching unit owners failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'UnitOwner' } });
      }
    };
    if (shouldFetchUnitOwner) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchUnitOwner]);

  useEffect(() => {
    const fetchAndSet = async () => {
      const idsAtFetchStart = new Set(contactLoader.map(({ id }) => id));
      contextDispatch({ type: 'IS_FETCHING_CONTACT' });
      try {
        const result = await fetchContacts(getTableClientId(clientId!, ENTITY_MODEL_NAME_BUILDING_OWNER));
        contextDispatch({ type: 'FETCHED_CONTACT', payload: { contacts: result, idsAtFetchStart } });
      } catch (error) {
        console.error('[contacts] fetching contacts failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'Contact' } });
      }
    };
    if (shouldFetchContact) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchContact]);

  useSubscriptions<
    OnCreateBuildingOwnerSubscription,
    OnUpdateBuildingOwnerSubscription,
    OnDeleteBuildingOwnerSubscription
  >(
    onCreateBuildingOwner,
    onUpdateBuildingOwner,
    onDeleteBuildingOwner,
    (data) => {
      contextDispatch({
        type: 'ADD_BUILDING_OWNER',
        payload: { buildingOwner: data.onCreateBuildingOwner as BuildingOwner },
      });
    },
    (data) => {
      contextDispatch({
        type: 'UPDATE_BUILDING_OWNER',
        payload: { buildingOwner: data.onUpdateBuildingOwner as BuildingOwner },
      });
    },
    (data) => {
      const { id } = data.onDeleteBuildingOwner as BuildingOwner;
      contextDispatch({
        type: 'DELETE_BUILDING_OWNER',
        payload: { id },
      });
    }
  );
  useSubscriptions<OnCreateUnitOwnerSubscription, OnUpdateUnitOwnerSubscription, OnDeleteUnitOwnerSubscription>(
    onCreateUnitOwner,
    onUpdateUnitOwner,
    onDeleteUnitOwner,
    (data) => {
      contextDispatch({
        type: 'ADD_UNIT_OWNER',
        payload: { unitOwner: data.onCreateUnitOwner as UnitOwner },
      });
    },
    (data) => {
      contextDispatch({
        type: 'UPDATE_UNIT_OWNER',
        payload: { unitOwner: data.onUpdateUnitOwner as UnitOwner },
      });
    },
    (data) => {
      const { id } = data.onDeleteUnitOwner as UnitOwner;
      contextDispatch({
        type: 'DELETE_UNIT_OWNER',
        payload: { id },
      });
    }
  );
  useSubscriptions<OnCreateContactSubscription, OnUpdateContactSubscription, OnDeleteContactSubscription>(
    onCreateContact,
    onUpdateContact,
    onDeleteContact,
    (data) => {
      contextDispatch({
        type: 'ADD_CONTACT',
        payload: { contact: data.onCreateContact as Contact },
      });
    },
    (data) => {
      contextDispatch({
        type: 'UPDATE_CONTACT',
        payload: { contact: data.onUpdateContact as Contact },
      });
    },
    (data) => {
      const { id } = data.onDeleteContact as Contact;
      contextDispatch({
        type: 'DELETE_CONTACT',
        payload: { id },
      });
    }
  );

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === CONTACT_TABLE_NAME
      ) {
        setFetchContacts();
      }
    },
    () => {},
    () => {}
  );

  const setFetchBuildingOwners = () => {
    contextDispatch({ type: 'SHOULD_FETCH_BUILDING_OWNER' });
  };

  const setFetchUnitOwners = () => {
    contextDispatch({ type: 'SHOULD_FETCH_UNIT_OWNER' });
  };

  const setFetchContacts = () => {
    contextDispatch({ type: 'SHOULD_FETCH_CONTACT' });
  };

  const createContact = async (input: CreateContact): Promise<Contact> => {
    const { address, ...remainingInput } = input;
    if (address && address.street) {
      const newAddress = await createAddress({
        ...(cleanInputCreate(address) as Omit<Address, 'id' | 'clientId' | 'readId'>),
      });
      remainingInput.addressId = newAddress.id;
    }

    const cleanedInput = cleanInputCreate(remainingInput) as Omit<Contact, 'id' | 'clientId' | 'readId'>;
    const contact = await mutation<Contact, CreateMutationVariables>(createMutation, {
      input: {
        ...cleanedInput,
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME),
      },
    });
    contextDispatch({ type: 'ADD_CONTACT', payload: { contact } });
    return contact;
  };

  const createContactWithoutAddress = async (
    input: Omit<Contact, 'id' | 'clientId' | 'readId' | 'address'>
  ): Promise<Contact> => {
    const contact = await createContact(input);
    return contact;
  };

  const inviteContactToClientAccount = async (contact: Contact): Promise<MutationStatus> => {
    const isTenant = contactContainsType(contact, ContactType.TENANT);
    const isOwner = contactContainsType(contact, ContactType.OWNER);
    if (isNilOrEmpty(contact.email) || (!isOwner && !isTenant)) {
      return { status: false, id: '' };
    }
    const { data, error } = await inviteMember({
      email: contact.email,
      role: isOwner ? 'owner' : 'tenant',
      contactId: contact.id,
      invitationKind: isOwner ? 'OWNER' : 'TENANT',
      resend: true,
    });
    if (error || !data) return { status: false, id: '' };
    await queryClient.invalidateQueries({ queryKey: pendingInvitationsQueryKey });
    await queryClient.invalidateQueries({ queryKey: workspaceInvitationsQueryKey(clientId) });
    return { status: true, id: data.id };
  };

  const addTypeToContactIdIfAllowed = async (contactId: string, newType: ContactType) => {
    const contact = getContact(contactId);
    if (contact) {
      await addTypeIfAllowed(contact, newType);
    }
  };

  const addOwnerTypeIfAllowed = async (contact: Contact) => await addTypeIfAllowed(contact, ContactType.OWNER);

  const addTypeIfAllowed = async (contact: Contact, newType: ContactType) => {
    if (COMPANY_ONLY_NEW_CONTACT_TYPES.includes(newType) && contact.profileType !== ContactProfileType.COMPANY) {
      return contact;
    }
    const allowedTypes = ALLOWED_TYPES_BEFORE_NEW_TYPE[newType] ?? [];

    const isAllowed =
      !contactContainsType(contact, newType) && contact.types?.some((type) => allowedTypes.includes(type));
    if (!isAllowed) {
      return contact;
    }
    const typesWithoutOther = contact.types.filter((currentType) => currentType !== ContactType.OTHER);
    return await updateContact(contact, { types: [...typesWithoutOther, newType] });
  };

  const updateContact = async (original: Contact, updates: Partial<Contact>) => {
    const newStatus = getContactStatus(updates);
    if (newStatus) {
      updates.status = newStatus;
    }
    const updatesWithoutAuthFields = omit(updates, ['readers', 'readId', 'clientId', ...BACKEND_ONLY_CONTACT_FIELDS]);
    const result = await mutationWithFetchBefore<Contact>(original.id, getQuery, updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, ...updatesWithoutAuthFields }, false, true) },
    });
    contextDispatch({ type: 'UPDATE_CONTACT', payload: { contact: result } });
    return result;
  };

  const setContact = (contact: Contact) =>
    contextDispatch({
      type: 'UPDATE_CONTACT',
      payload: { contact: cleanInputUpdate(contact, false, true) as Contact },
    });

  const refreshContact = async (id: string) => {
    try {
      const contact = await get<Contact>(CONTACT_QUERY, id);
      if (isNil(contact)) {
        return;
      }
      setContact(contact);
    } catch (error) {
      console.error('[contacts] refreshing contact failed', id, error);
    }
  };

  const deleteContact = async (id: string) => {
    if (!contactsDetailsDelete) {
      return;
    }

    await deleteEntityWithFetchBefore<Pick<Contact, 'id'>, DeleteMutationVariables>({ id }, getQuery, deleteMutation);
    contextDispatch({ type: 'DELETE_BUILDING', payload: { id } });
  };

  const createBuildingOwner = async (
    input: Omit<BuildingOwner, 'id'> | Omit<BuildingOwner, 'id' | 'clientId' | 'readId'>
  ) => {
    const buildingOwner = await mutation<BuildingOwner, CreateBuildingOwnerMutationVariables>(
      createBuildingOwnerMutation,
      {
        input: {
          ...(cleanInputCreate(input) as CreateBuildingOwnerInput),
          clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME_BUILDING_OWNER),
          readId: getReadId(clientId!, ENTITY_MODEL_NAME_BUILDING_OWNER),
        },
      }
    );
    contextDispatch({ type: 'ADD_BUILDING_OWNER', payload: { buildingOwner } });
    return buildingOwner;
  };

  const updateBuildingOwner = async (original: BuildingOwner, updates: Partial<BuildingOwner>) => {
    const result = await mutation<BuildingOwner>(updateBuildingOwnerMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) },
    });
    contextDispatch({ type: 'UPDATE_BUILDING_OWNER', payload: { buildingOwner: result } });
  };

  const deleteBuildingOwner = async (id: string) => {
    if (!buildingsUnitsDetailsDelete) {
      return;
    }
    const fromStore = buildingOwners.find((bo) => bo.id === id);
    if (!isNil(fromStore)) {
      const ownerUser = users.find((user) => user.contactId === fromStore.contactId);
      if (!isNil(ownerUser)) {
        await mutation(handleUserRightsChange, {
          input: {
            userId: ownerUser.id,
            event: UserRightChangeEvent.DELETE_OWNER_BUILDING_ACCESS,
            clientId,
            buildingId: fromStore.buildingId,
          },
        });
      }
    }
    await deleteAndHideEntity<BuildingOwner>(fromStore!, deleteBuildingOwnerMutation);
  };

  const checkCompanyPeppolStatus = async (id: string): Promise<PeppolRegistrationCheck> => {
    try {
      const result = await mutation<VerifyPeppolContactResponse>(verifyPeppolContactMutation, {
        input: { contactId: id },
      });
      return result.isContactVerified ? PeppolRegistrationCheck.REGISTERED : PeppolRegistrationCheck.NOT_REGISTERED;
    } catch (_error) {
      console.error('Error checking Peppol status');
      return PeppolRegistrationCheck.UNAVAILABLE;
    }
  };

  const deleteUnitOwner = async (id: string) => {
    if (!buildingsUnitsDetailsDelete) {
      return;
    }
    const fromStore = unitOwners.find((uo) => uo.id === id);
    if (!isNil(fromStore)) {
      const ownerUser = users.find((user) => user.contactId === fromStore.ownerId);
      if (!isNil(ownerUser)) {
        await mutation(handleUserRightsChange, {
          input: {
            userId: ownerUser.id,
            event: UserRightChangeEvent.DELETE_OWNER_UNIT_ACCESS,
            clientId,
            unitId: fromStore.unitId,
          },
        });
      }
    }
    await deleteAndHideEntity<UnitOwner>(fromStore!, deleteUnitOwnerMutation);
  };

  const updateUnitOwner = async (original: UnitOwner, updates: Partial<UnitOwner>) => {
    const result = await mutation<UnitOwner>(updateUnitOwnerMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) },
    });
    contextDispatch({ type: 'UPDATE_UNIT_OWNER', payload: { unitOwner: result } });
  };

  const createUnitOwner = async (input: Omit<UnitOwner, 'id' | 'clientId' | 'readId'>): Promise<UnitOwner> => {
    const unitOwner = await mutation<UnitOwner, CreateUnitOwnerMutationVariables>(createUnitOwnerMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateUnitOwnerInput),
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME_UNIT_OWNER),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME_UNIT_OWNER),
      },
    });
    contextDispatch({ type: 'ADD_UNIT_OWNER', payload: { unitOwner } });
    return unitOwner;
  };

  const getContact = (id: string) => {
    return contacts.contacts.find((c) => c.id === id) ?? contacts.ocrDrafts.find((c) => c.id === id);
  };

  const getOrCreateJointOwnership = async (existingJointOwnerships: Contact[], ownerIds: string[]) => {
    const existingJointOwnership = getExistingJointOwnershipOfOwners(existingJointOwnerships, ownerIds);
    let jointOwnership: Contact;
    if (existingJointOwnership) {
      // Already existing joint ownership
      jointOwnership = existingJointOwnership;
    } else {
      const [jointOwnershipName, jointOwners] = ownerIds.reduce(
        (acc: [string, JointOwner[]], ownerId) => {
          const contact = getContact(ownerId);
          if (contact) {
            const contactName = getContactNameFromObject(contact);
            if (acc[0] === '') acc[0] = contactName;
            else acc[0] = `${acc[0]}, ${contactName}`;
          }
          acc[1].push({ contactId: ownerId, contactPerson: true });
          return acc;
        },
        ['', []]
      );
      // Create joint ownership
      const ownerCommunicationSettingsProfileId = getDefaultOwnerCommunicationSettingsProfiles()?.[0]?.id ?? 'mockId';
      jointOwnership = await createContactWithoutAddress({
        types: [ContactType.JOINT_OWNERSHIP],
        profileType: ContactProfileType.COMPANY,
        jointOwners,
        companyName: jointOwnershipName,
        sendInvitation: false,
        communicationSettingsProfileId: ownerCommunicationSettingsProfileId,
      });
    }
    return jointOwnership;
  };

  const deepDeleteContact = async (contactId: string) => {
    if (!contactsDetailsDelete) {
      return;
    }
    return await mutation<MutationStatus, DeleteEntityByIdMutationVariables>(deleteEntityByIdMutation, {
      input: { entityId: contactId, entity: DeepDeletableEntityType.CONTACT },
    });
  };

  const values = useMemo(
    () => ({
      ...contacts,
      createContact,
      createContactWithoutAddress,
      getContact,
      createBuildingOwner,
      updateBuildingOwner,
      deleteBuildingOwner,
      deleteUnitOwner,
      updateUnitOwner,
      createUnitOwner,
      updateContact,
      refreshContact,
      setContact,
      deleteContact,
      deepDeleteContact,
      inviteContactToClientAccount,
      addOwnerTypeIfAllowed,
      addTypeIfAllowed,
      addTypeToContactIdIfAllowed,
      createAddress,
      updateAddress,
      deleteAddress,
      getOrCreateJointOwnership,
      contactsError: undefined,
      contactsLoading: loading,
      setFetchContacts,
      setFetchBuildingOwners,
      setFetchUnitOwners,
      checkCompanyPeppolStatus,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contacts, loading]
  );

  return <ContactContext.Provider value={values}>{children}</ContactContext.Provider>;
};

export const useContacts = (): ContactContext => {
  const context = useContext<ContactContext | null>(ContactContext);

  if (isNil(context)) {
    throw new Error('`useContacts` hook must be used within a `ContactContextProvider` component');
  }

  return context as ContactContext;
};
